// Sections are written in the Map's own iteration order — since
// parseInpSections builds that map by walking the file top to bottom, a
// re-export preserves the file's original section order for free.

import { getElementCommentKey } from "./parseInp.ts";

/**
 * Quotes a token only if it actually needs it — matches `tokenize`'s own
 * unquoting, so a value that never needed quoting round-trips byte-identical.
 *
 * The `.inp` format has no escape for a literal `"` inside a quoted token —
 * `tokenize` (and the real engine's own `getTokens`, `input.c`) both just
 * scan for the FIRST following `"` to close it, nothing more. A token that
 * needs quoting (contains whitespace) AND itself contains a `"` can't be
 * written unescapably without corrupting the round-trip (the embedded quote
 * would be misread as the closing one, splitting the rest of the line into
 * garbage tokens) — the embedded quote is dropped here instead, same
 * "degrade gracefully rather than corrupt neighboring data" spirit as
 * `writeOrDeleteSection`'s own drop-if-empty convention.
 */
function formatToken(token: string): string {
  if (!/\s/.test(token)) return token || '""';
  return `"${token.replace(/"/g, "")}"`;
}

/**
 * `comments` is `parseInpComments`'s own map (or a project's persisted
 * copy of it) — re-attaches each row's own trailing `;comment`, tracking
 * the same per-section, per-name occurrence count `parseInpComments`
 * built the key with, so a multi-row-per-name section (`[CURVES]`,
 * `[TIMESERIES]`...) reattaches the right line's own comment, not just
 * the first one. Omitted entirely (not `;` with empty text) for a row
 * with no recorded comment — this deliberately does NOT round-trip a
 * fully-commented line (a `;`-only line was never captured in the first
 * place, by design — see `parseInpComments`'s own header).
 */
export function serializeInp(
  rawSections: ReadonlyMap<string, string[][]>,
  comments?: ReadonlyMap<string, string>
): string {
  const lines: string[] = [];
  for (const [name, rows] of rawSections) {
    lines.push(`[${name}]`);
    const occurrences = new Map<string, number>();
    for (const row of rows) {
      const line = name === "TITLE" ? row[0] : row.map(formatToken).join("  ");
      if (!comments || comments.size === 0) {
        lines.push(line);
        continue;
      }
      const rowName = name === "TITLE" ? "" : (row[0] ?? "");
      const occurrence = occurrences.get(rowName) ?? 0;
      occurrences.set(rowName, occurrence + 1);
      const comment = comments.get(getElementCommentKey(name, rowName, occurrence));
      lines.push(comment === undefined ? line : `${line}  ;${comment}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
